export type ConversionLineaState = {
  /** Tope del renglón (Cantidad + Cantidad B). */
  total: number;
  /** Remanente en el tenant origen. */
  cantidad: number;
  /** Porción a enviar al otro tenant. */
  cantidadB: number;
  /** Porcentaje de Cantidad B sobre el total (entero hacia abajo). */
  porcentaje: number;
};

const floorNonNeg = (n: number): number => {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
};

const clamp = (n: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, n));

export function porcentajeDesdeCantidades(cantidadB: number, total: number): number {
  const t = floorNonNeg(total);
  if (t <= 0) return 0;
  return floorNonNeg((floorNonNeg(cantidadB) / t) * 100);
}

export function initConversionLinea(
  cantidad: number | null | undefined,
  cantidadB?: number | null,
  porcentaje?: number | null,
): ConversionLineaState {
  const b = floorNonNeg(Number(cantidadB ?? 0));
  const cant = floorNonNeg(Number(cantidad ?? 0));
  const total = cant + b;
  const pctFromArgs = porcentaje != null && Number(porcentaje) > 0
    ? floorNonNeg(Number(porcentaje))
    : null;
  return {
    total,
    cantidad: cant,
    cantidadB: b,
    porcentaje: pctFromArgs != null ? clamp(pctFromArgs, 0, 100) : porcentajeDesdeCantidades(b, total),
  };
}

/** Cambia Cantidad B (0…total); recalcula remanente y %. */
export function aplicarCantidadB(
  state: ConversionLineaState,
  cantidadBNueva: number,
): ConversionLineaState {
  const total = floorNonNeg(state.total);
  const b = clamp(floorNonNeg(cantidadBNueva), 0, total);
  return {
    total,
    cantidad: total - b,
    cantidadB: b,
    porcentaje: porcentajeDesdeCantidades(b, total),
  };
}

/** Cambia el remanente (0…total); recalcula B y %. */
export function aplicarCantidad(
  state: ConversionLineaState,
  cantidadNueva: number,
): ConversionLineaState {
  const total = floorNonNeg(state.total);
  const cant = clamp(floorNonNeg(cantidadNueva), 0, total);
  const b = total - cant;
  return {
    total,
    cantidad: cant,
    cantidadB: b,
    porcentaje: porcentajeDesdeCantidades(b, total),
  };
}

/** Cambia el % (0…100); recalcula B y remanente. */
export function aplicarPorcentaje(
  state: ConversionLineaState,
  porcentajeNuevo: number,
): ConversionLineaState {
  const total = floorNonNeg(state.total);
  const pct = clamp(floorNonNeg(porcentajeNuevo), 0, 100);
  const b = clamp(floorNonNeg((total * pct) / 100), 0, total);
  return {
    total,
    cantidad: total - b,
    cantidadB: b,
    porcentaje: pct,
  };
}

/**
 * Arma el estado de una línea a partir de lo persistido.
 * Si hay %, recalcula Cantidad/Cant.B con floor sobre el total (cant+B),
 * para no heredar redondeos “hacia arriba” del backend.
 */
export function repartirLineaConversion(
  cantidad: number | null | undefined,
  cantidadB?: number | null,
  porcentaje?: number | null,
): ConversionLineaState {
  const base = initConversionLinea(cantidad, cantidadB, null);
  const pct = porcentaje != null && Number(porcentaje) > 0
    ? floorNonNeg(Number(porcentaje))
    : 0;
  if (pct > 0) return aplicarPorcentaje(base, pct);
  return base;
}
